/**
 * FinalizeTab — the job's money lifecycle in one place (Finalize phase 1, 048).
 *
 *   1. Finalize: a popup asks the invoice date and the final weight (long
 *      distance) or final hours + crew (local hourly). That opens an invoice
 *      draft. Until it is issued the draft reads the CURRENT estimate (rate,
 *      crew, minimum, every line) and keeps only what happened on the day.
 *      THE ESTIMATE IS NEVER CHANGED by anything here.
 *   2. Draft editor + live preview, side by side. Edits save behind the click
 *      (useRowSync, one row: the draft). The preview runs calcFinal HERE on the
 *      current estimate's inputs — the same engine the server prices with, so the
 *      figure is instant and identical. The server's own `calc` is the check: once
 *      a save settles, a disagreement is shown rather than hidden.
 *   3. Issue invoice → POST /leads/:id/documents { draft_id }. The server prices
 *      the stored draft again and refuses while anything blocks it.
 *   4. After issue: the compact breakdown of the locked invoice (live paid and
 *      balance) and "Preview invoice" for the client's own page. Correction is void
 *      (admin, in the invoice list) → reopen the draft → edit → issue again.
 *
 * Payments and the invoice list live beneath (BillingSections view 'finalize').
 * Visible to every user: nothing in the draft payload is admin-only.
 *
 * ADMIN-ONLY SECTIONS (phase 2, 049): "Contractor" — what the contractor is paid
 * on this job (the same lines as Dispatch, editable here even once approved —
 * §8.2 — plus the tip, 100 % to the crew). A local job's contractor pay is
 * settled automatically at issue (§9.9); long distance keeps the accept / keep
 * decision when the invoice's final weight would change an approved estimate; "Commission" — the
 * sales rep, their standard %, this job's override, the figure the job earns
 * (a preview until the invoice is issued, then the recorded commission, owed or
 * reversed, with what has been paid against it). Read from
 * /api/finalize-admin, which refuses non-admins; the section is not rendered for
 * them either.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { tintOf } from '../lib/theme';
import { numberInput, STEP_PER_LB } from '../lib/numberInput';
import { useApi, useApiClient } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { useRowSync } from '../hooks/useRowSync';
import { useAction } from '../hooks/useAction';
import { calcEstimate, calcFinal, CalcFinal, FinalInputs, FinalDraftFee, FinalDraft, FinalLineEdit, FinalLineChange, finalLineChanges, accessorialKey, finalBilledHours, roundUpToQuarterHour, calcContractorEstimate, contractorFinalOf, findAdditionalTruckFee } from '../lib/estimate';
import { ContractorLinesTable } from './ContractorLines';
import { HistoryButton, HistoryPopup, HistoryRow } from './HistoryPopup';
import { dvpQuote, DVP_NAME, premiumOverrideOf } from '../lib/dvp';
import { formatMoney, formatDate } from '../lib/utils';
import { formatDocNo } from '../lib/jobNumber';
import { BillingSections } from './BillingSections';
import { InvoiceView } from '../pages/portal/InvoiceView';
import { PORTAL_CSS } from '../pages/portal/portalStyles';
import { InlineInput, HoursMinutesInput, hmShort, cardStyle, cardTitle, fieldLabel, unitLabel } from './TimeInputs';
import type { InvoiceDraftPayload, InvoiceDraftOpen, InvoiceDraftNone, HourlyOpening } from '../services/api';

const num = (v: unknown): number => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const present = (v: unknown) => v != null && v !== '';
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
/** Today in the browser's own time zone — the invoice date the rep means. */
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const TIP_PCTS = [10, 15, 20];

interface FinalizeTabProps {
  leadId: string;
  lead: any;
  tariff: any;
  /** The stage moves on issue (Booked → Invoiced): re-read the lead header. */
  onLeadChanged: () => void;
  /** Admins: open Dispatch at the contractor estimate (a client line changed on the invoice, 062). */
  onOpenDispatch?: () => void;
}

export function FinalizeTab({ leadId, lead, tariff, onLeadChanged, onOpenDispatch }: FinalizeTabProps) {
  const api = useApiClient();
  const { data, loading, error, refresh } = useApi<InvoiceDraftPayload>(
    async () => (await api.getInvoiceDraft(leadId)).data!,
    { dependencies: [leadId] },
  );
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [billingKey, setBillingKey] = useState(0);
  const [refreshErr, setRefreshErr] = useState<string | null>(null);
  // The admin section re-reads whenever the draft or the invoice changes (its
  // preview follows the final weight; issue / void change the reprice).
  const adminKey = `${billingKey}|${data?.draft?.id ?? ''}|${data?.draft?.updatedAt ?? ''}|${(data as any)?.invoiced ?? ''}|${(data as any)?.currentInvoice?.id ?? ''}`;

  const quietRefresh = async () => {
    try { await refresh(); setRefreshErr(null); }
    catch (e: any) { setRefreshErr(e?.message || 'Could not refresh the draft.'); }
  };
  // The invoice just issued, offered for sending (the popup lives here, not in
  // DraftEditor: the draft is gone once the refresh lands, and so is the editor).
  const [justIssued, setJustIssued] = useState<string | null>(null);
  const afterIssue = async (docId: string | null) => {
    await quietRefresh();
    setBillingKey((k) => k + 1);
    onLeadChanged();
    if (docId) setJustIssued(docId);
  };

  return (
    <div>
      <div style={{ marginBottom: '22px' }}>
        <h3 style={sectionHead}>Finalize</h3>
        {refreshErr && <Notice tone="red">{refreshErr}</Notice>}
        {loading && !data ? (
          <Card><div style={{ color: 'var(--crm-dim)', fontSize: '12px' }}>Loading…</div></Card>
        ) : error && !data ? (
          <Card><div style={{ color: 'var(--crm-red)', fontSize: '12px' }}>{error}</div></Card>
        ) : data?.draft ? (
          <DraftEditor
            key={data.draft.id}
            leadId={leadId}
            lead={lead}
            onOpenDispatch={isAdmin ? onOpenDispatch : undefined}
            payload={data as InvoiceDraftOpen}
            refresh={refresh}
            onDiscarded={quietRefresh}
            onIssued={afterIssue}
          />
        ) : data ? (
          <NoDraft leadId={leadId} payload={data as InvoiceDraftNone} onOpened={quietRefresh} />
        ) : null}
      </div>

      {isAdmin && <AdminSections leadId={leadId} lead={lead} reloadKey={adminKey} />}

      <BillingSections
        leadId={leadId}
        lead={lead}
        tariff={tariff}
        view="finalize"
        reloadKey={billingKey}
        // A void (or any status change) in the invoice list changes what this
        // tab offers: after a void, "Reopen draft" appears.
        onDocumentsChanged={() => { void quietRefresh(); onLeadChanged(); }}
        // A payment can close the job or clear "Square cost needed" in the header.
        onPaymentsChanged={onLeadChanged}
      />

      {justIssued && (
        <SendIssuedModal
          leadId={leadId}
          docId={justIssued}
          email={String(lead?.email ?? '').trim()}
          // A send marks the invoice sent: the invoice list and the status line re-read.
          onSent={() => { setBillingKey((k) => k + 1); void quietRefresh(); }}
          onClose={() => setJustIssued(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// No open draft: Finalize, or the issued invoice, or reopen after a void
// ============================================================================

function NoDraft({ leadId, payload, onOpened }: { leadId: string; payload: InvoiceDraftNone; onOpened: () => Promise<void> }) {
  const api = useApiClient();
  const [popup, setPopup] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const reopenGuard = useAction(); // before the early return below: hooks run every render
  const [msg, setMsg] = useState<string | null>(null);

  if (payload.invoiced) {
    const inv = payload.currentInvoice;
    return (
      <Card>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--crm-text)' }}>
            Invoice {inv ? formatDocNo(String(inv.number)) : ''} issued
          </span>
          {inv && (
            <span style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>
              {invoiceStatusLabel(inv.status)}{inv.figures?.issueDate ? ` · invoice date ${formatDate(String(inv.figures.issueDate).slice(0, 10))}` : ''}
            </span>
          )}
          {inv && <span style={{ flex: 1 }} />}
          {inv && <button onClick={() => setPreviewOpen(true)} style={secondaryBtn}>Preview invoice</button>}
        </div>
        {inv?.figures && <Breakdown data={breakdownFromFigures(inv.figures)} />}
        <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginTop: '10px' }}>
          The invoice is locked. To correct it, an admin voids it in the Invoices list below; its draft can then be reopened, edited and issued again.
        </div>
        {previewOpen && inv && <InvoicePreviewModal source={{ kind: 'issued', docId: inv.id }} onClose={() => setPreviewOpen(false)} />}
      </Card>
    );
  }

  const reopen = () => reopenGuard.run(() => reopenNow());
  const reopenNow = async () => {
    if (!payload.lastIssued) return;
    setBusy(true); setMsg(null);
    try { await api.reopenInvoiceDraft(payload.lastIssued.id); await onOpened(); }
    catch (e: any) { setMsg(e?.message || 'Could not reopen the draft.'); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      {payload.lastIssued ? (
        <>
          <div style={{ fontSize: '13px', color: 'var(--crm-text)', fontWeight: 600, marginBottom: '6px' }}>The last invoice was voided.</div>
          <div style={{ fontSize: '12px', color: 'var(--crm-muted)', marginBottom: '12px' }}>
            Reopen its draft to correct what was billed: it keeps the times, fees, adjustments and tip, and reads the current estimate for everything else.
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={reopen} disabled={busy} style={primaryBtn}>{busy ? 'Reopening…' : 'Reopen draft'}</button>
            {payload.canFinalize && <button onClick={() => setPopup(true)} disabled={busy} style={secondaryBtn}>Finalize from the estimate</button>}
          </div>
          {!payload.canFinalize && (
            <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginTop: '8px' }}>
              Reopening works at any stage. Finalizing again from the estimate is available once the job is Booked.
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ fontSize: '12px', color: 'var(--crm-muted)', marginBottom: '12px' }}>
            {payload.mode === 'ld'
              ? 'Enter the final weight to price the invoice at the estimate’s rates.'
              : payload.mode === 'hourly'
                ? 'Enter the real hours and crew to price the invoice at the estimate’s hourly rate.'
                : 'Open an invoice draft from the estimate.'}{' '}
            The estimate itself is never changed.
          </div>
          {payload.canFinalize
            ? <button onClick={() => setPopup(true)} style={primaryBtn}>Finalize</button>
            : <Notice tone="amber">Finalize is available once the job is Booked{payload.stage ? ` (it is ${payload.stage})` : ''}.</Notice>}
        </>
      )}
      {msg && <Notice tone="red">{msg}</Notice>}
      {popup && <FinalizePopup leadId={leadId} payload={payload} onClose={() => setPopup(false)} onOpened={async () => { setPopup(false); await onOpened(); }} />}
    </Card>
  );
}

function FinalizePopup({ leadId, payload, onClose, onOpened }: {
  leadId: string; payload: InvoiceDraftNone; onClose: () => void; onOpened: () => Promise<void>;
}) {
  const api = useApiClient();
  const o = payload.opening;
  const [invoiceDate, setInvoiceDate] = useState(localToday());
  const [weight, setWeight] = useState(o.weightLbs != null ? String(o.weightLbs) : '');
  const [time, setTime] = useState<FinalTimeValue>(() => openingTime(o));
  const billed = billedOf(time).billedHours;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const isLd = payload.mode === 'ld';
  const isHourly = payload.mode === 'hourly';
  const ok =
    /^\d{4}-\d{2}-\d{2}$/.test(invoiceDate)
    && (!isLd || num(weight) > 0)
    && (!isHourly || (num(billed) > 0 && num(o.hourlyRate) > 0));

  const openGuard = useAction();
  const submit = () => openGuard.run(() => submitNow());
  const submitNow = async () => {
    if (!ok) return;
    setBusy(true); setMsg(null);
    try {
      await api.openInvoiceDraft(leadId, {
        invoice_date: invoiceDate,
        ...(isLd ? { final_weight_lbs: num(weight) } : {}),
        ...(isHourly ? {
          final_travel_hours: (time.travelMin ?? 0) / 60,
          final_onsite_hours: (time.onsiteMin ?? 0) / 60,
          final_minimum_hours: time.minimumHours,
          final_billable_hours: time.billableOverride,
          final_crew_size: time.crew,
          final_trucks: time.trucks,
        } : {}),
      });
      await onOpened();
    } catch (e: any) {
      setMsg(e?.message || 'Could not open the draft.');
      setBusy(false);
    }
  };

  return (
    <Modal title="Finalize" onClose={onClose} width={isHourly ? '640px' : undefined}>
      <Field label="Invoice date">
        <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} style={input} />
        <Hint>Becomes the invoice’s recognition date (its revenue books to this month).</Hint>
      </Field>
      {isLd && (
        <Field label="Final weight (lb)">
          <input {...numberInput(1)} min={0} value={weight} autoFocus onChange={(e) => setWeight(e.target.value)} style={input} />
          <Hint>Estimate: {o.weightLbs != null ? `${num(o.weightLbs).toLocaleString()} lb` : 'not set'}. The estimate’s rates apply to this weight.</Hint>
        </Field>
      )}
      {isHourly && (
        <div style={{ marginBottom: '12px' }}>
          <FinalTimeCards value={time} estimate={o} onChange={(p) => setTime((t) => ({ ...t, ...p }))} />
        </div>
      )}
      {isHourly && !(num(o.hourlyRate) > 0) && (
        <div style={{ marginBottom: '12px' }}><Notice tone="amber">Set the crew and rate on the estimate.</Notice></div>
      )}
      {msg && <Notice tone="red">{msg}</Notice>}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={secondaryBtn}>Cancel</button>
        <button onClick={submit} disabled={!ok || busy} style={{ ...primaryBtn, opacity: ok ? 1 : 0.5 }}>{busy ? 'Opening…' : 'Open draft'}</button>
      </div>
    </Modal>
  );
}

// ============================================================================
// The open draft: editor + live preview
// ============================================================================

/** The draft as one editable row (the PATCH field names). */
interface DraftRow {
  id: string;
  invoice_date: string;
  final_weight_lbs: number | null;
  final_hours: number | null;
  final_crew_size: number | null;
  final_travel_hours: number | null;
  final_onsite_hours: number | null;
  final_minimum_hours: number | null;
  final_billable_hours: number | null;
  final_trucks: number | null;
  adjustments: { id: string; description: string; amount: number; taxable: boolean; sort?: number }[];
  fees: FinalDraftFee[];
  line_edits: Record<string, FinalLineEdit>;
  tip_mode: 'none' | 'pct' | 'amount';
  tip_pct: number | null;
  tip_amount: number | null;
  notes: string | null;
  dvp_reprice: boolean;
}

const rowOf = (p: InvoiceDraftOpen): DraftRow => ({
  id: p.draft.id,
  invoice_date: p.draft.invoiceDate,
  final_weight_lbs: p.draft.finalWeightLbs,
  final_hours: p.draft.finalHours,
  final_crew_size: p.draft.finalCrewSize,
  final_travel_hours: p.draft.finalTravelHours ?? null,
  final_onsite_hours: p.draft.finalOnsiteHours ?? null,
  final_minimum_hours: p.draft.finalMinimumHours ?? null,
  final_billable_hours: p.draft.finalBillableHours ?? null,
  final_trucks: p.draft.finalTrucks ?? null,
  adjustments: p.draft.adjustments ?? [],
  fees: p.draft.fees ?? [],
  line_edits: p.draft.lineEdits ?? {},
  tip_mode: p.draft.tipMode,
  tip_pct: p.draft.tipPct,
  tip_amount: p.draft.tipAmount,
  notes: p.draft.notes,
  dvp_reprice: p.draft.dvpReprice,
});

interface LocalDvp { itemId: string; estimatePremium: number; finalPremium: number; declaredValue: number; applied: boolean }

/**
 * The DVP reprice exactly as lib/invoiceDraft.ts computeDraft does it: the
 * premium line re-priced at the final weight with the company's current rates,
 * carried to calcFinal as a line rate when the toggle is on. A premium the
 * office fixed (061) is never repriced.
 */
function dvpOf(inputs: any, row: DraftRow, rates: any): { dvp: LocalDvp | null; lineRates: Record<string, number> } {
  const lineRates: Record<string, number> = {};
  const item = (inputs.items ?? []).find((i: any) => i.source === 'custom' && String(i.description ?? '').startsWith(DVP_NAME));
  if (!item?.id || inputs.mode !== 'ld' || !present(row.final_weight_lbs) || premiumOverrideOf(inputs.dvp) != null) return { dvp: null, lineRates };
  const q = dvpQuote({ ...inputs.lead, ...(inputs.dvp ?? {}), weight_lbs: num(row.final_weight_lbs) }, rates);
  const applied = row.dvp_reprice !== false;
  if (applied) lineRates[String(item.id)] = q.premium;
  return {
    dvp: { itemId: String(item.id), estimatePremium: num(item.rate_snapshot ?? item.amount), finalPremium: round2(q.premium), declaredValue: round2(q.declaredValue), applied },
    lineRates,
  };
}

function DraftEditor({ leadId, lead, onOpenDispatch, payload, refresh, onDiscarded, onIssued }: {
  leadId: string;
  lead: any;
  onOpenDispatch?: () => void;
  payload: InvoiceDraftOpen;
  refresh: () => Promise<void>;
  onDiscarded: () => Promise<void>;
  onIssued: (docId: string | null) => Promise<void>;
}) {
  const api = useApiClient();
  const sync = useRowSync<DraftRow>({
    update: (id, patch) => api.updateInvoiceDraft(id, patch),
    onSettled: refresh,
  });
  const row = sync.view([rowOf(payload)])[0];
  const rowState = row as DraftRow & { _key: string; _status: string; _error: string | null };
  const edit = (patch: Partial<DraftRow>) => sync.edit(rowState._key, patch);

  const inputs = payload.inputs as FinalInputs & { mode: string; dvp: any };
  const mode = payload.mode;
  const est = payload.estimate;

  // ---- The live preview: calcFinal on the current estimate, this instant ----
  const { dvp, lineRates } = useMemo(() => dvpOf(inputs, row, payload.dvpRates), [inputs, row, payload.dvpRates]);
  const draftInput: FinalDraft = useMemo(() => ({
    finalWeightLbs: row.final_weight_lbs,
    finalHours: row.final_hours,
    finalCrewSize: row.final_crew_size,
    finalTravelHours: row.final_travel_hours,
    finalOnsiteHours: row.final_onsite_hours,
    // Not changed on the draft → the estimate's minimum (as the server does).
    finalMinimumHours: row.final_minimum_hours ?? est.minimumHours,
    finalBillableHours: row.final_billable_hours,
    finalTrucks: row.final_trucks,
    lineRates,
    lineEdits: row.line_edits,
    adjustments: row.adjustments,
    fees: row.fees,
    tipMode: row.tip_mode,
    tipPct: row.tip_pct,
    tipAmount: row.tip_amount,
  }), [row, lineRates, est.minimumHours]);
  const calc: CalcFinal = useMemo(() => calcFinal(inputs, draftInput), [inputs, draftInput]);
  // 062: the same draft without its line edits — each estimate line as it
  // would bill untouched — and what the edits changed (quoted → billed).
  const unedited: CalcFinal = useMemo(() => calcFinal(inputs, { ...draftInput, lineEdits: null }), [inputs, draftInput]);
  const lineChanges = useMemo(() => finalLineChanges(inputs, row.line_edits, calc), [inputs, row.line_edits, calc]);
  // The estimate as it is now, for "estimate → final".
  const estimateTotal = useMemo(
    () => calcEstimate(inputs.lead, inputs.items, inputs.tariff, inputs.branch, inputs.fees, inputs.accessorials ?? []).total,
    [inputs],
  );

  // Same engine on both sides, so once the saves have settled the server's
  // figure must equal this one. If it ever does not, say so — the server's is
  // the one issuing would lock.
  const serverTotal = num(payload.calc?.total);
  const mismatch = !sync.busy && Math.abs(serverTotal - calc.total) >= 0.005;

  const blocks = payload.blocks;
  const warnings = payload.warnings;
  const [confirming, setConfirming] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const canIssue = blocks.length === 0 && !sync.busy && !sync.failed && !mismatch;

  // One issue at a time; the key makes a repeat return the first invoice.
  const issueGuard = useAction();
  const issue = () => issueGuard.run((key) => issueNow(key));
  const issueNow = async (key: string) => {
    setBusy('issue'); setMsg(null);
    try {
      if (!(await sync.flushAll())) { setMsg('An edit did not save — retry it before issuing.'); return; }
      const res = await api.withIdempotencyKey(key).issueInvoiceFromDraft(leadId, payload.draft.id);
      setConfirming(false);
      await onIssued(res.data?.id ?? null);
    } catch (e: any) {
      setMsg(e?.message || 'The invoice was not issued.');
      try { await refresh(); } catch { /* the message above is the finding */ }
    } finally { setBusy(null); }
  };

  const discardGuard = useAction();
  const discard = () => discardGuard.run(() => discardNow());
  const discardNow = async () => {
    if (!window.confirm('Discard this draft? The estimate is unchanged; you can finalize again.')) return;
    setBusy('discard'); setMsg(null);
    try { await sync.flushAll(); await api.discardInvoiceDraft(payload.draft.id); await onDiscarded(); }
    catch (e: any) { setMsg(e?.message || 'Could not discard the draft.'); setBusy(null); }
  };

  // A % tip that is not one of the buttons is a custom one; "Custom %" also
  // stays open after a click, so a preset value can be typed over.
  const [customPct, setCustomPct] = useState(() => row.tip_mode === 'pct' && !TIP_PCTS.includes(num(row.tip_pct)));

  // ---- Adjustments ----
  const adjustments = row.adjustments ?? [];
  const setAdjustments = (list: DraftRow['adjustments']) => edit({ adjustments: list.map((a, i) => ({ ...a, sort: i })) });
  const addAdjustment = () => setAdjustments([
    ...adjustments,
    { id: `adj-${Date.now().toString(36)}`, description: '', amount: 0, taxable: true },
  ]);

  // ---- Fees from the catalog (055) ----
  // Client values are copied in when added (the server copies them again from
  // the catalog — it never takes a price from here). On a local job the
  // contractor's share follows on its own; reps see nothing of it.
  const { data: catalog } = useApi<any[]>(async () => (await api.getFees()).data || []);
  const draftFees = row.fees ?? [];
  const truckFeeId = mode === 'hourly' ? findAdditionalTruckFee(catalog ?? [])?.id : undefined;
  const pickable = (catalog ?? []).filter((f) => f.active !== false && f.id !== truckFeeId);
  const [dupOf, setDupOf] = useState<{ fee: any; existing: string } | null>(null);
  const setFees = (list: FinalDraftFee[]) => edit({ fees: list.map((f, i) => ({ ...f, sort: i })) });
  const addFee = (fee: any) => {
    setDupOf(null);
    setFees([...draftFees, {
      id: `dfee-${Date.now().toString(36)}`, fee_id: fee.id, name: fee.name ?? 'Fee', fee_type: fee.fee_type ?? 'flat',
      amount: fee.amount ?? null, rate: fee.rate ?? null, percentage: fee.percentage ?? null,
      unit_label: fee.unit_label ?? null, applied_to: fee.applied_to ?? 'base',
      minimum_amount: fee.minimum_amount ?? null, free_units: fee.free_units ?? null, quantity: 1,
    }]);
  };
  // Access fees (062): long distance only, priced from the tariff's CLIENT
  // accessorial rates — the estimate's own pricing (free allowance, % floor).
  const accessRates = mode === 'ld'
    ? ((inputs.accessorials ?? []) as any[]).filter((r) => r.active !== false && (r.payer ?? 'client') === 'client')
    : [];
  const addAccess = (r: any) => {
    setDupOf(null);
    const key = accessorialKey(r.leg, r.accessorial);
    const method = r.pricing_method ?? 'flat';
    const recorded = method === 'unit' ? recordedAccessUnits(lead, r.accessorial, r.leg) : 0;
    setFees([...draftFees, {
      id: `dfee-${Date.now().toString(36)}`, fee_id: null, access_key: key, name: r.label ?? key,
      fee_type: method === 'unit' ? 'unit' : method === 'pct' ? 'pct' : 'flat',
      amount: r.amount ?? null, rate: r.rate ?? null, percentage: r.percentage ?? null,
      unit_label: r.unit_label ?? null, applied_to: 'base',
      minimum_amount: r.minimum_amount ?? null, free_units: r.free_units ?? null,
      quantity: recorded > 0 ? recorded : 1,
    }]);
  };
  const pickFee = (id: string) => {
    if (id.startsWith('access:')) {
      const r = accessRates.find((x) => `access:${accessorialKey(x.leg, x.accessorial)}` === id);
      if (!r) return;
      const existing = accessAlreadyCharged(accessorialKey(r.leg, r.accessorial), calc, draftFees);
      if (existing) setDupOf({ fee: { ...r, name: r.label, __access: true }, existing });
      else addAccess(r);
      return;
    }
    const fee = pickable.find((f) => f.id === id);
    if (!fee) return;
    const existing = alreadyCharged(fee, calc);
    if (existing) setDupOf({ fee, existing });
    else addFee(fee);
  };

  // ---- Estimate lines edited on the draft only (062) ----
  const dvpItemId = dvp?.itemId ?? ((inputs.items ?? []) as any[]).find((i) => i.source === 'custom' && String(i.description ?? '').startsWith(DVP_NAME))?.id;
  const editableLines = unedited.items.filter((l) => l.id && !l.adjustment && !l.draftFeeId && l.id !== dvpItemId && l.id !== 'final-additional-truck');
  const setLineEdit = (id: string, patch: FinalLineEdit | null) => {
    const base = unedited.items.find((l) => l.id === id);
    const next: Record<string, FinalLineEdit> = { ...(row.line_edits ?? {}) };
    const merged: FinalLineEdit = patch == null ? {} : { ...(next[id] ?? {}), ...patch };
    // Kept only while it differs from the estimate.
    if (base && present(merged.quantity) && num(merged.quantity) === num(base.rawQuantity)) delete merged.quantity;
    if (base && present(merged.rate) && num(merged.rate) === num(base.rate)) delete merged.rate;
    if (!merged.removed) delete merged.removed;
    if (Object.keys(merged).length) next[id] = merged; else delete next[id];
    edit({ line_edits: next });
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: '14px', alignItems: 'start' }}>
      {/* ================= EDITOR ================= */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text)' }}>Invoice draft</span>
          <SaveState status={rowState._status} error={rowState._error} busy={sync.busy} onRetry={() => sync.retry(rowState._key)} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: mode === 'hourly' ? '1fr' : '1fr 1fr', gap: '10px' }}>
          <Field label="Invoice date">
            <input type="date" value={row.invoice_date ?? ''} onChange={(e) => e.target.value && edit({ invoice_date: e.target.value })} style={input} />
          </Field>
          {mode === 'ld' && (
            <Field label="Final weight (lb)">
              <NumInput value={row.final_weight_lbs} onCommit={(v) => edit({ final_weight_lbs: v })} />
            </Field>
          )}
        </div>
        {mode === 'hourly' && (
          <div style={{ marginBottom: '6px' }}>
            <FinalTimeCards value={draftTime(row, est)} estimate={est} onChange={(p) => edit(draftTimePatch(p))} />
          </div>
        )}
        <Hint>
          {mode === 'ld' && <>Estimate weight {est.weightLbs != null ? `${num(est.weightLbs).toLocaleString()} lb` : 'not set'}. </>}
          {mode === 'hourly' && <>Rate {formatMoney(est.hourlyRate)}/hr. </>}
          Rates, crew and lines come from the estimate as it is now.
        </Hint>

        {/* DVP at the final weight */}
        {dvp && (
          <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '12px', color: 'var(--crm-text)', margin: '12px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={dvp.applied} onChange={(e) => edit({ dvp_reprice: e.target.checked })} style={{ marginTop: '2px' }} />
            <span>
              Reprice {DVP_NAME} at the final weight
              <span style={{ display: 'block', color: 'var(--crm-muted)', fontSize: '11px', fontFamily: mono }}>
                estimate {formatMoney(dvp.estimatePremium)} → final {formatMoney(dvp.finalPremium)} (declared {formatMoney(dvp.declaredValue)})
              </span>
            </span>
          </label>
        )}

        {/* Estimate lines (062): edit or remove for this invoice only */}
        <div style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', gap: '8px' }}>
            <span style={labelStyle}>Estimate lines</span>
            <span style={{ fontSize: '10px', color: 'var(--crm-dim)' }}>This invoice only — the estimate stays as quoted</span>
          </div>
          {editableLines.map((l) => {
            const id = l.id as string;
            const e = row.line_edits?.[id];
            const now = calc.items.find((x) => x.id === id);
            const removed = !!e?.removed;
            const changed = !!e;
            const isBase = l.source === 'base';
            const qtyEditable = !isBase && l.rateKind !== 'pct';
            const rateSuffix = l.rateKind === 'pct' ? '%' : l.unitLabel ? `/${l.unitLabel}` : '';
            return (
              <div key={id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 76px 96px 80px 28px', gap: '6px', alignItems: 'center', marginBottom: '6px', opacity: removed ? 0.55 : 1 }}>
                <span style={{ fontSize: '12px', color: changed ? 'var(--crm-amber)' : 'var(--crm-text)', textDecoration: removed ? 'line-through' : 'none', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} title={l.desc}>{l.desc}</span>
                {qtyEditable && !removed ? (
                  <InlineInput value={present(e?.quantity) ? num(e?.quantity) : num(l.rawQuantity)} type="number" width="70px" align="right"
                    highlight={present(e?.quantity)} onCommit={(v) => { const n = Number(v); if (v.trim() !== '' && Number.isFinite(n) && n >= 0) setLineEdit(id, { quantity: n }); }} />
                ) : <span style={{ fontSize: '11px', color: 'var(--crm-dim)', textAlign: 'right' }}>{isBase && l.unitLabel ? `${num(l.quantity).toLocaleString()} ${l.unitLabel}` : ''}</span>}
                {!removed ? (
                  <InlineInput value={present(e?.rate) ? num(e?.rate) : num(l.rate)} type="number" width="64px" align="right"
                    step={l.unitLabel === 'lb' ? STEP_PER_LB : 1} negative={l.source === 'custom'}
                    prefix={l.rateKind === 'pct' ? undefined : '$'} suffix={rateSuffix || undefined}
                    highlight={present(e?.rate)} onCommit={(v) => { const n = Number(v); if (v.trim() !== '' && Number.isFinite(n)) setLineEdit(id, { rate: n }); }} />
                ) : <span />}
                <span style={{ fontSize: '12px', fontFamily: mono, color: changed ? 'var(--crm-amber)' : 'var(--crm-text)', textAlign: 'right' }} title={changed ? `Estimate ${formatMoney(l.net)}` : undefined}>
                  {removed ? 'removed' : formatMoney(now?.net ?? 0)}
                </span>
                {isBase ? (
                  changed ? <button onClick={() => setLineEdit(id, null)} title="Back to the estimate" aria-label={`Restore ${l.desc}`} style={{ ...ghostBtn, padding: '4px 6px' }}>↺</button> : <span />
                ) : removed || changed ? (
                  <button onClick={() => setLineEdit(id, null)} title="Back to the estimate" aria-label={`Restore ${l.desc}`} style={{ ...ghostBtn, padding: '4px 6px' }}>↺</button>
                ) : (
                  <button onClick={() => setLineEdit(id, { removed: true })} title="Remove from this invoice" aria-label={`Remove ${l.desc}`} style={{ ...ghostBtn, padding: '4px 6px', color: 'var(--crm-red)' }}>×</button>
                )}
              </div>
            );
          })}
          {dvp && <Hint>{DVP_NAME} follows the reprice option above.</Hint>}
        </div>

        {/* Fees from the catalog (055) */}
        <div style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', gap: '8px' }}>
            <span style={labelStyle}>Fees</span>
            <select value="" onChange={(e) => pickFee(e.target.value)} style={{ ...ghostBtn, maxWidth: '180px' }} aria-label="Add fee">
              <option value="">+ Add fee</option>
              {accessRates.length > 0 ? (
                <>
                  <optgroup label="Access fees">
                    {accessRates.map((r) => <option key={r.id} value={`access:${accessorialKey(r.leg, r.accessorial)}`}>{r.label ?? accessorialKey(r.leg, r.accessorial)}</option>)}
                  </optgroup>
                  <optgroup label="Fees">
                    {pickable.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </optgroup>
                </>
              ) : pickable.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          {dupOf && (
            <Notice tone="amber">
              Already charged: {dupOf.existing}. Add “{dupOf.fee.name}” anyway?
              <span style={{ display: 'inline-flex', gap: '6px', marginLeft: '8px' }}>
                <button onClick={() => (dupOf.fee.__access ? addAccess(dupOf.fee) : addFee(dupOf.fee))} style={ghostBtn}>Add anyway</button>
                <button onClick={() => setDupOf(null)} style={ghostBtn}>Cancel</button>
              </span>
            </Notice>
          )}
          {draftFees.length === 0 && <div style={{ fontSize: '11px', color: 'var(--crm-dim)' }}>None. Add a fee for extra work done on the day (piano, boxes…{mode === 'ld' ? ', a long carry or stairs that were not quoted' : ''}). For a one-off amount use an adjustment.</div>}
          {draftFees.map((f, i) => {
            const line = calc.items.find((l) => l.draftFeeId === f.id);
            // The rate it was added with ($, $/unit or %), and the one billed if edited.
            const addedRate = num(f.fee_type === 'unit' ? f.rate : f.fee_type === 'pct' ? f.percentage : f.amount);
            const rateEdited = present(f.rate_override) && num(f.rate_override) !== addedRate;
            const setFee = (patch: Partial<FinalDraftFee>) => setFees(draftFees.map((x, j) => (j === i ? { ...x, ...patch } : x)));
            const unitSuffix = f.fee_type === 'pct' ? '%' : f.fee_type === 'unit' ? `/${f.unit_label ?? 'unit'}` : '';
            return (
              <div key={f.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 76px 96px 80px auto', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: rateEdited ? 'var(--crm-amber)' : 'var(--crm-text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title={num(f.free_units) > 0 ? `${f.name} — first ${num(f.free_units)} ${f.unit_label ?? 'units'} free` : f.name ?? undefined}>
                  {f.name}{num(f.free_units) > 0 && <span style={{ color: 'var(--crm-dim)', fontSize: '10px' }}> · {num(f.free_units)} free</span>}
                </span>
                {f.fee_type !== 'pct' ? (
                  <InlineInput value={num(f.quantity ?? 1)} type="number" width="70px" align="right"
                    onCommit={(v) => { const n = Number(v); if (v.trim() !== '' && Number.isFinite(n) && n > 0) setFee({ quantity: n }); }} />
                ) : <span />}
                <InlineInput value={rateEdited ? num(f.rate_override) : addedRate} type="number" width="64px" align="right"
                  step={f.unit_label === 'lb' ? STEP_PER_LB : 1}
                  prefix={f.fee_type === 'pct' ? undefined : '$'} suffix={unitSuffix || undefined} highlight={rateEdited}
                  onCommit={(v) => { const n = Number(v); if (v.trim() !== '' && Number.isFinite(n) && n >= 0) setFee({ rate_override: n === addedRate ? null : n }); }} />
                <span style={{ fontSize: '12px', fontFamily: mono, color: rateEdited ? 'var(--crm-amber)' : 'var(--crm-text)', textAlign: 'right' }}>{formatMoney(line?.net ?? 0)}</span>
                <span style={{ display: 'inline-flex', gap: '4px' }}>
                {rateEdited && (
                  <button onClick={() => setFee({ rate_override: null })} title={`Back to the rate it was added with (${f.fee_type === 'pct' ? `${addedRate}%` : formatMoney(addedRate)})`} aria-label={`Restore ${f.name} rate`} style={{ ...ghostBtn, padding: '4px 6px' }}>↺</button>
                )}
                  <button onClick={() => setFees(draftFees.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`} title="Remove from this invoice" style={{ ...ghostBtn, padding: '4px 6px', color: 'var(--crm-red)' }}>×</button>
                </span>
              </div>
            );
          })}
        </div>

        {/* Adjustments */}
        <div style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={labelStyle}>Adjustments</span>
            <button onClick={addAdjustment} style={ghostBtn}>+ Add line</button>
          </div>
          {adjustments.length === 0 && <div style={{ fontSize: '11px', color: 'var(--crm-dim)' }}>None. A positive amount is a charge, a negative one a discount.</div>}
          {adjustments.map((a, i) => (
            <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '1fr 100px auto auto', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
              <input
                value={a.description}
                placeholder={num(a.amount) < 0 ? 'Discount' : 'Additional charge'}
                onChange={(e) => setAdjustments(adjustments.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
                style={input}
              />
              <NumInput value={a.amount} allowNegative onCommit={(v) => setAdjustments(adjustments.map((x, j) => (j === i ? { ...x, amount: v ?? 0 } : x)))} />
              <label title="Taxable adjustments join the subtotal before tax; non-taxable ones are added after tax" style={{ fontSize: '11px', color: 'var(--crm-muted)', display: 'flex', gap: '4px', alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={a.taxable} onChange={(e) => setAdjustments(adjustments.map((x, j) => (j === i ? { ...x, taxable: e.target.checked } : x)))} />
                taxable
              </label>
              <button onClick={() => setAdjustments(adjustments.filter((_, j) => j !== i))} aria-label="Remove adjustment" style={{ ...ghostBtn, color: 'var(--crm-red)' }}>×</button>
            </div>
          ))}
        </div>

        {/* Tip */}
        <div style={{ marginTop: '14px' }}>
          <span style={labelStyle}>Tip</span>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginTop: '6px' }}>
            <Pill active={row.tip_mode === 'none'} onClick={() => edit({ tip_mode: 'none' })}>None</Pill>
            {TIP_PCTS.map((p) => (
              <Pill key={p} active={row.tip_mode === 'pct' && !customPct && num(row.tip_pct) === p} onClick={() => { setCustomPct(false); edit({ tip_mode: 'pct', tip_pct: p }); }}>{p}%</Pill>
            ))}
            <Pill active={row.tip_mode === 'pct' && customPct} onClick={() => { setCustomPct(true); edit({ tip_mode: 'pct', tip_pct: row.tip_pct ?? 0 }); }}>Custom %</Pill>
            {row.tip_mode === 'pct' && customPct && (
              <div style={{ width: '90px' }}>
                <NumInput value={row.tip_pct} step={0.5} onCommit={(v) => edit({ tip_pct: v == null ? 0 : Math.min(100, v) })} />
              </div>
            )}
            <Pill active={row.tip_mode === 'amount'} onClick={() => { setCustomPct(false); edit({ tip_mode: 'amount', tip_amount: row.tip_amount ?? 0 }); }}>Custom $</Pill>
            {row.tip_mode === 'amount' && (
              <div style={{ width: '110px' }}>
                <NumInput value={row.tip_amount} onCommit={(v) => edit({ tip_amount: v })} />
              </div>
            )}
          </div>
          <Hint>
            {row.tip_mode === 'pct'
              ? `${num(row.tip_pct)}% of the pre-tax subtotal ${formatMoney(calc.tipBase)} = ${formatMoney(calc.tip.amount)}. `
              : ''}
            Untaxed — its own line after tax on the invoice; owed to the contractor, not revenue.
            {!payload.tipsEnabled && ' A tip can be entered now, but issuing with one is refused until the tip accounting ships.'}
          </Hint>
          {calc.tip.amount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginTop: '6px', padding: '6px 9px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px' }}>
              <span style={{ color: 'var(--crm-muted)' }}>Tip {formatMoney(calc.tip.amount)} → contractor</span>
              <span style={{ color: 'var(--crm-text)', fontWeight: 600 }}>100%</span>
            </div>
          )}
        </div>

        {/* Notes */}
        <Field label="Internal notes (not printed on the invoice)" style={{ marginTop: '14px' }}>
          <textarea
            value={row.notes ?? ''}
            onChange={(e) => edit({ notes: e.target.value })}
            rows={2}
            style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
          <button onClick={discard} disabled={!!busy} style={{ ...ghostBtn, color: 'var(--crm-red)', borderColor: 'var(--crm-red-soft-line)' }}>
            {busy === 'discard' ? 'Discarding…' : 'Discard draft'}
          </button>
        </div>
      </Card>

      {/* ================= PREVIEW + ISSUE ================= */}
      <div>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text)' }}>Final breakdown</span>
            <span style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>
              {formatDate(row.invoice_date)}
              {mode === 'ld' && calc.finalWeightLbs != null && ` · ${calc.finalWeightLbs.toLocaleString()} lb${calc.billableWeight > calc.finalWeightLbs ? ` (billed at the ${calc.billableWeight.toLocaleString()} lb minimum)` : ''}`}
              {mode === 'hourly' && calc.finalHours != null && ` · ${calc.finalHours.toFixed(2)} hrs billed${calc.finalCrewSize ? `, crew ${calc.finalCrewSize}` : ''}`}
            </span>
          </div>
          <div style={{ opacity: sync.busy ? 0.85 : 1 }}>
            <Breakdown data={breakdownFromCalc(calc, payload.payments, lineChanges)} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--crm-dim)', fontSize: '11px' }}>
              Estimate {formatMoney(estimateTotal)} · {calc.total - estimateTotal >= 0 ? '+' : '−'}{formatMoney(Math.abs(calc.total - estimateTotal))}
            </span>
            {/* Saves first, so the preview is built from what the server holds. */}
            <button onClick={async () => { if (await sync.flushAll()) setPreviewOpen(true); }} title="The client's invoice as it will be issued" style={secondaryBtn}>
              Preview invoice
            </button>
          </div>
          {previewOpen && <InvoicePreviewModal source={{ kind: 'draft', draftId: payload.draft.id }} onClose={() => setPreviewOpen(false)} />}
        </Card>

        <div style={{ marginTop: '12px' }}>
          {/* 062: the contractor estimate never follows a line edit — an admin reviews it. */}
          {(payload.contractorNotices ?? []).map((n) => (
            <Notice key={n.key} tone="amber">
              {n.text}
              {onOpenDispatch
                ? <> — <button onClick={onOpenDispatch} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--crm-accent)', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}>open the contractor estimate</button></>
                : '. An admin reviews it on Dispatch.'}
            </Notice>
          ))}
          {blocks.map((b) => <Notice key={b} tone="red">{b}</Notice>)}
          {warnings.map((w) => <Notice key={w} tone="amber">{w}</Notice>)}
          {mismatch && (
            <Notice tone="red">
              The preview ({formatMoney(calc.total)}) differs from the server’s price ({formatMoney(serverTotal)}). Issuing locks the server’s figure — reload the page before issuing.
            </Notice>
          )}
          {msg && <Notice tone="red">{msg}</Notice>}

          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              disabled={!canIssue || !!busy}
              title={blocks.length ? 'Resolve the items above first' : sync.busy ? 'Saving…' : ''}
              style={{ ...primaryBtn, width: '100%', padding: '10px', fontSize: '13px', opacity: canIssue ? 1 : 0.45, cursor: canIssue ? 'pointer' : 'default' }}
            >
              {sync.busy ? 'Saving…' : 'Issue invoice…'}
            </button>
          ) : (
            <div style={{ background: 'var(--crm-accent-tint)', border: '1px solid var(--crm-accent-soft-line)', borderRadius: '10px', padding: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text)', marginBottom: '8px' }}>Issue this invoice?</div>
              <div style={{ fontSize: '12px', fontFamily: mono }}>
                <Row label="Subtotal" value={formatMoney(calc.discountedSubtotal)} />
                <Row label={`Tax (${num(calc.taxRate)}%)`} value={formatMoney(calc.tax)} />
                {calc.nonTaxableAdjustments !== 0 && <Row label="Non-taxable adjustments" value={formatMoney(calc.nonTaxableAdjustments)} />}
                {calc.tip.amount > 0 && <Row label="Tip" value={formatMoney(calc.tip.amount)} />}
                <Row label="Invoice total" value={formatMoney(calc.total)} bold />
              </div>
              <div style={{ fontSize: '11px', color: 'var(--crm-muted)', margin: '8px 0 10px' }}>
                Invoice date {formatDate(row.invoice_date)}. The invoice is locked once issued, the job moves to Invoiced, and revenue is recognized on that date. Correcting it later means an admin voids it.
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={() => setConfirming(false)} disabled={!!busy} style={secondaryBtn}>Cancel</button>
                <button onClick={issue} disabled={!!busy || !canIssue} style={primaryBtn}>{busy === 'issue' ? 'Issuing…' : `Issue for ${formatMoney(calc.total)}`}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- The compact final breakdown --------------------------------------------------

interface BreakdownData {
  /** `changed`: an estimate line edited on the draft (062); `removed`: shown struck through, not billed. */
  lines: { desc: string; qty: string | null; amount: number; tag?: string | null; changed?: boolean; removed?: boolean }[];
  /** Before any discount (line + order level), as the invoice prints it. */
  subtotal: number;
  discount: number;
  taxRate: number;
  tax: number;
  /** Non-taxable adjustments and the tip — after tax, inside the total. */
  afterTax: { label: string; amount: number }[];
  ccFeeTotal: number;
  /** Everything the client owes, card fees included (the invoice's own "Invoice total"). */
  total: number;
  paidTotal: number;
  balanceDue: number;
}

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** "3,400 lb × $0.79", "2 × $75.00", "15%" — or null when the line is just an amount. */
function qtyText(l: { quantity?: any; rate?: any; rateKind?: string; unitLabel?: string | null; desc?: string }): string | null {
  const q = num(l.quantity);
  const rate = num(l.rate);
  if (l.rateKind === 'pct') return rate ? `${rate}%` : null;
  if (l.unitLabel && q) return `${q.toLocaleString()} ${l.unitLabel} × ${formatMoney(rate)}`;
  if (l.rateKind === 'unit' && q) return `${q.toLocaleString()} × ${formatMoney(rate)}`;
  if (q > 1 && rate && !String(l.desc ?? '').includes('×')) return `${q.toLocaleString()} × ${formatMoney(rate)}`;
  return null;
}

/**
 * Is this fee already on the bill? The same catalog fee, a line of the same
 * name, or an access charge it looks like (the keywords of backend
 * lib/accessorials.ts ACCESSORIAL_PATTERNS). Only a WARNING — "add anyway".
 */
const ACCESS_PATTERNS: { key: string; re: RegExp }[] = [
  { key: 'elevator', re: /elevat/i },
  { key: 'shuttle', re: /shuttle/i },
  { key: 'stairs', re: /stair|flight/i },
  { key: 'long_walk', re: /long\s*(walk|carry)|walk\s*(fee|charge)|long\s*haul/i },
];
/** An access fee already on the bill (062): the estimate's line for that key (not removed), or one added on the draft. */
function accessAlreadyCharged(key: string, calc: CalcFinal, draftFees: FinalDraftFee[]): string | null {
  const line = calc.items.find((l) => l.autoKey === key);
  if (line) return line.desc;
  const added = draftFees.find((f) => f.access_key === key);
  return added ? added.name ?? key : null;
}

/** The units the lead records for an access condition — the estimate's own reading (backend lib/accessorials recordedUnits). */
function recordedAccessUnits(lead: any, accessorial: string, leg: string): number {
  if (accessorial === 'stairs') return Number(lead?.[`${leg}_stairs`]) || 0;
  if (accessorial === 'long_walk') return Number(lead?.[`${leg}_walk_ft`]) || 0;
  return lead?.[`${leg}_${accessorial}`] ? 1 : 0;
}

function alreadyCharged(fee: any, calc: CalcFinal): string | null {
  const norm = (v: unknown) => String(v ?? '').trim().toLowerCase();
  const access = ACCESS_PATTERNS.find((p) => p.re.test(String(fee.name ?? '')));
  const hit = calc.items.find((l) => l.source !== 'base' && !l.adjustment && (
    (l.feeId && l.feeId === fee.id)
    || norm(l.desc) === norm(fee.name)
    || (access && l.autoKey && l.autoKey.endsWith(`:${access.key}`))
  ));
  return hit ? hit.desc : null;
}

/** The draft's breakdown from the local calcFinal, with payments as the invoice counts them. */
function breakdownFromCalc(calc: CalcFinal, pays: { paidTotal: number; ccFeeTotal: number } | undefined, changes: FinalLineChange[] = []): BreakdownData {
  const lineDiscounts = calc.items.reduce((t, l) => t + num(l.discountAmt), 0);
  const cc = num(pays?.ccFeeTotal);
  const paid = num(pays?.paidTotal);
  const total = r2(calc.total + cc);
  const afterTax = [
    ...calc.adjustments.filter((a) => !a.taxable).map((a) => ({ label: a.description || (a.amount < 0 ? 'Discount' : 'Additional charge'), amount: a.amount })),
    ...(calc.tip.amount > 0 ? [{ label: calc.tip.mode === 'pct' ? `Tip (${calc.tip.pct}%)` : 'Tip', amount: calc.tip.amount }] : []),
  ];
  return {
    lines: [
      ...calc.items.map((l) => {
        const ch = l.id ? changes.find((c) => c.itemId === l.id) : undefined;
        return {
          desc: l.desc, qty: qtyText(l), amount: l.gross,
          tag: l.adjustment ? 'adjustment' : ch ? `changed from estimate: ${formatMoney(ch.from)} → ${formatMoney(ch.to ?? 0)}` : null,
          changed: !!ch,
        };
      }),
      ...changes.filter((c) => c.to == null).map((c) => ({
        desc: c.desc, qty: null, amount: 0, tag: `changed from estimate: ${formatMoney(c.from)} → removed`, changed: true, removed: true,
      })),
    ],
    subtotal: r2(calc.subtotal + lineDiscounts),
    discount: r2(lineDiscounts + calc.totalDiscountAmt),
    taxRate: num(calc.taxRate),
    tax: calc.tax,
    afterTax,
    ccFeeTotal: cc,
    total,
    paidTotal: paid,
    balanceDue: r2(total - paid),
  };
}

/** The issued invoice's breakdown from invoiceFigures — the portal's own numbers. */
function breakdownFromFigures(f: any): BreakdownData {
  return {
    lines: (f.items ?? []).map((l: any) => ({ desc: l.desc, qty: qtyText(l), amount: num(l.gross), tag: l.adjustment ? 'adjustment' : l.note ?? null })),
    subtotal: num(f.grossSubtotal),
    discount: num(f.allDiscountAmt),
    taxRate: num(f.taxRate),
    tax: num(f.tax),
    afterTax: (f.afterTax ?? []).map((r: any) => ({ label: r.label, amount: num(r.amount) })),
    ccFeeTotal: num(f.ccFeeTotal),
    total: num(f.total),
    paidTotal: num(f.paidTotal),
    balanceDue: num(f.balanceDue),
  };
}

function Breakdown({ data: d }: { data: BreakdownData }) {
  const signed = (n: number) => (n < 0 ? `−${formatMoney(Math.abs(n))}` : formatMoney(n));
  return (
    <div style={{ fontSize: '12px' }}>
      {d.lines.map((l, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '10px', padding: '4px 0', borderBottom: '1px solid var(--crm-panel-2)', alignItems: 'baseline' }}>
          <span style={{ color: l.removed ? 'var(--crm-dim)' : 'var(--crm-text)', minWidth: 0 }}>
            <span style={{ textDecoration: l.removed ? 'line-through' : 'none' }}>{l.desc}</span>
            {l.tag && <span style={{ color: l.changed ? 'var(--crm-amber)' : 'var(--crm-muted)', fontSize: '10px' }}> · {l.tag}</span>}
          </span>
          <span style={{ color: 'var(--crm-muted)', fontSize: '11px', fontFamily: mono, whiteSpace: 'nowrap' }}>{l.qty ?? ''}</span>
          <span style={{ fontFamily: mono, color: l.removed ? 'var(--crm-dim)' : l.amount < 0 ? 'var(--crm-amber)' : 'var(--crm-text)', whiteSpace: 'nowrap', textAlign: 'right', minWidth: '84px' }}>{l.removed ? '—' : signed(l.amount)}</span>
        </div>
      ))}
      <div style={{ fontFamily: mono, marginTop: '8px' }}>
        <Row label="Subtotal" value={formatMoney(d.subtotal)} />
        {d.discount > 0 && <Row label="Discounts" value={`−${formatMoney(d.discount)}`} />}
        <Row label={`Tax${d.taxRate ? ` (${d.taxRate}%)` : ''}`} value={formatMoney(d.tax)} />
        {d.afterTax.map((r, i) => <Row key={i} label={r.label} value={signed(r.amount)} />)}
        {d.ccFeeTotal > 0 && <Row label="Card fees (card payments only)" value={formatMoney(d.ccFeeTotal)} />}
        <div style={{ borderTop: '1px solid var(--crm-line)', marginTop: '6px', paddingTop: '6px' }}>
          <Row label="Invoice total" value={formatMoney(d.total)} bold big />
        </div>
        <Row label="Paid" value={formatMoney(d.paidTotal)} />
        <Row label="Balance" value={formatMoney(d.balanceDue)} bold />
      </div>
    </div>
  );
}

/** documents.status 'draft' on an invoice only means "not emailed yet" — never show DRAFT for an issued invoice. */
const invoiceStatusLabel = (status: string) =>
  status === 'paid' ? 'PAID' : status === 'sent' ? 'ISSUED · SENT' : 'ISSUED · NOT SENT YET';

// ---- "Preview invoice" ----------------------------------------------------------------

/**
 * The client-facing invoice, in a modal.
 *   issued: the real client link (print view) — exactly what the client opens,
 *           with "Open in new tab".
 *   draft:  the portal's own InvoiceView, fed by GET /invoice-drafts/:id/preview,
 *           which builds the payload with the portal's builder from an unsaved
 *           invoice row. Same page; no number until it is issued.
 */
function InvoicePreviewModal({ source, onClose }: { source: { kind: 'issued'; docId: string } | { kind: 'draft'; draftId: string }; onClose: () => void }) {
  const api = useApiClient();
  const [url, setUrl] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const key = source.kind === 'issued' ? source.docId : source.draftId;
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (source.kind === 'issued') {
          const u = (await api.createDocumentPortalLink(source.docId)).data?.url;
          if (!alive) return;
          if (u) setUrl(u); else setErr('Could not open the client view of this invoice.');
        } else {
          const d = (await api.previewInvoiceDraft(source.draftId)).data;
          if (alive) setData(d);
        }
      } catch (e: any) {
        if (alive) setErr(e?.message || 'Could not open the invoice preview.');
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, api]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '12px', width: '880px', maxWidth: '100%', height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderBottom: '1px solid var(--crm-line)' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text)' }}>
            {source.kind === 'draft' ? 'Invoice preview — not issued yet' : 'Client invoice'}
          </span>
          <span style={{ flex: 1 }} />
          {url && <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: 'var(--crm-accent)' }}>Open in new tab ↗</a>}
          <button onClick={onClose} aria-label="Close" style={{ ...ghostBtn, fontSize: '13px' }}>×</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', background: 'var(--crm-white)' }}>
          {err ? <div style={{ padding: '30px', color: 'var(--crm-red)', fontSize: '12px' }}>{err}</div>
          : source.kind === 'issued'
            ? (url
                ? <iframe title="Client invoice" src={`${url}${url.includes('?') ? '&' : '?'}print=1`} sandbox="allow-scripts allow-same-origin" style={{ width: '100%', height: '100%', border: 'none', background: 'var(--crm-white)' }} />
                : <div style={{ padding: '30px', color: 'var(--crm-dim)', fontSize: '12px' }}>Loading the client’s view…</div>)
            : (data
                ? (
                  // The portal's stylesheet is scoped to .pp, so it does not leak into the app.
                  <div className="pp">
                    <style>{PORTAL_CSS}</style>
                    <main className="pp-main"><InvoiceView data={data} pdfPath="" preview /></main>
                  </div>
                )
                : <div style={{ padding: '30px', color: 'var(--crm-dim)', fontSize: '12px' }}>Building the preview…</div>)}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Admin: Contractor (phase 2, 049; editable §8.2) — lines, tip, accept / keep
// ============================================================================

const fmtBasis = (b: { final_weight_lbs?: any; final_hours?: any; final_crew_size?: any; finalWeightLbs?: any; finalHours?: any; finalCrewSize?: any }) => {
  const w = b.final_weight_lbs ?? b.finalWeightLbs;
  const h = b.final_hours ?? b.finalHours;
  const c = b.final_crew_size ?? b.finalCrewSize;
  return w != null ? `${num(w).toLocaleString()} lb` : h != null ? `${num(h)} h${c ? `, crew ${c}` : ''}` : '—';
};
const REPRICE_STATUS: Record<string, string> = {
  applied: 'applied on issue', accepted: 'accepted', dismissed: 'kept the old total', superseded: 'withdrawn (invoice voided or replaced)', pending: 'waiting', edited: 'edited on Finalize',
};

/** One admin fetch for both sections; re-read when the draft or invoice changes. */
function AdminSections({ leadId, lead, reloadKey }: { leadId: string; lead: any; reloadKey: string }) {
  const api = useApiClient();
  const { data, error, refresh } = useApi<{ contractor: any; commission: any; margin: any }>(
    async () => (await api.getFinalizeAdmin(leadId)).data!,
    { dependencies: [leadId] },
  );
  const first = React.useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    refresh().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  return (
    <>
      <ContractorSection k={data?.contractor} cm={data?.commission} lead={lead} error={!data ? error : null} refresh={refresh} />
      <CommissionSection leadId={leadId} cm={data?.commission} error={!data ? error : null} refresh={refresh} />
      <MarginSection m={data?.margin} error={!data ? error : null} />
    </>
  );
}

function ContractorSection({ k, cm, lead, error, refresh }: { k: any; cm: any; lead: any; error: string | null; refresh: () => Promise<void> }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  // History popup: contractor pay changes on this job, and commissions reversed
  // when an invoice was voided. Newest first; the page keeps only working lists.
  const historyRows: HistoryRow[] = [
    ...(k?.history ?? []).map((r: any) => ({
      id: `r:${r.id}`, at: r.decided_at ?? r.created_at,
      text: `Contractor pay — ${r.kind === 'edit' ? r.note : `at the invoice's ${fmtBasis(r)}`}: ${formatMoney(r.old_total)} → ${formatMoney(r.new_total)}`,
      note: REPRICE_STATUS[r.status] ?? r.status,
      // Only a change that took effect moved the money.
      change: !['applied', 'accepted', 'edited'].includes(r.status) ? null : Math.round((num(r.new_total) - num(r.old_total)) * 100) / 100,
    })),
    ...(cm?.records ?? []).filter((r: any) => r.status === 'reversed').map((r: any) => ({
      id: `c:${r.id}`, at: r.reversedAt ?? r.recognitionDate,
      text: `Commission reversed (invoice voided): ${formatMoney(r.amount)} to ${r.repName}${r.paidAgainst > 0 ? ` — ${formatMoney(r.paidAgainst)} had been paid; netted against the rep's next payout` : ''}`,
      change: -num(r.amount),
    })),
  ];
  const api = useApiClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmDefaults, setConfirmDefaults] = useState(false);
  const { data: fees } = useApi<any[]>(async () => (await api.getFees()).data || []);
  const decideGuard = useAction();
  const decide = (kind: 'accept' | 'keep', id: string) => decideGuard.run(() => decideNow(kind, id));
  const decideNow = async (kind: 'accept' | 'keep', id: string) => {
    setBusy(kind); setMsg(null);
    try {
      if (kind === 'accept') await api.acceptContractorReprice(id); else await api.keepContractorReprice(id);
      await refresh();
    } catch (e: any) {
      setMsg(e?.message || 'That did not work.');
      refresh().catch(() => {});
    } finally { setBusy(null); }
  };

  // Lines: the same rows Dispatch edits, saved behind the click (~600 ms per
  // line, so typing "80" is one change — and one history row when approved).
  const sync = useRowSync<any>({
    update: (id, patch) => api.updateFinalizeContractorItem(id, patch),
    remove: (id) => api.deleteFinalizeContractorItem(id),
    onSettled: refresh,
  });
  const est = k?.estimate ?? null;
  const items = sync.view(est?.items ?? []);
  const lineState = new Map(items.map((it: any) => [it.id, it]));
  const keyOfLine = (id: string) => lineState.get(id)?._key ?? id;
  const final = est ? contractorFinalOf({ final_weight_lbs: est.finalWeightLbs, final_hours: est.finalHours, final_crew_size: est.finalCrewSize }) : null;
  const calc = calcContractorEstimate(items as any[], lead ?? {}, k?.accessorials ?? [], final);
  const approved = est?.status === 'approved';
  const tip = k?.tip ?? { amount: 0, status: 'none' };
  const totalWithTip = round2(calc.total + num(tip.amount));

  const lineGuard = useAction();
  const runLines = (label: string, fn: () => Promise<void>) => lineGuard.run(async () => {
    setBusy(label); setMsg(null);
    try {
      if (!(await sync.flushAll())) throw new Error('A line did not save — retry it first.');
      await fn();
      await refresh();
    } catch (e: any) {
      setMsg(e?.message || 'That did not work.');
      refresh().catch(() => {});
    } finally { setBusy(null); }
  });
  const addFee = (feeId: string) => runLines('add', async () => {
    try {
      await api.addFinalizeContractorItem(est.id, { fee_id: feeId });
    } catch (e: any) {
      // Same duplicate guard as Dispatch — warn, never block.
      if (e?.code !== 'DUPLICATE_ACCESSORIAL') throw e;
      if (!window.confirm(e.message)) return;
      await api.addFinalizeContractorItem(est.id, { fee_id: feeId }, true);
    }
  });
  const addCustom = () => runLines('add', async () => { await api.addFinalizeContractorItem(est.id, { description: 'Custom line', amount: 0 }); });
  const applyDefaults = () => runLines('defaults', async () => {
    await api.applyContractorLocalDefaults(lead.id);
    setConfirmDefaults(false);
  });

  return (
    <div style={{ marginBottom: '22px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
        <h3 style={sectionHead}>Contractor <span style={{ color: 'var(--crm-dim)', fontWeight: 400, textTransform: 'none' }}>· admin only</span></h3>
        {historyRows.length > 0 && <div style={{ marginBottom: '12px' }}><HistoryButton onClick={() => setHistoryOpen(true)} count={historyRows.length} /></div>}
      </div>
      {historyOpen && (
        <HistoryPopup title="Contractor and commission history" onClose={() => setHistoryOpen(false)}
          sub="Contractor pay changes on this job, and commissions reversed when an invoice was voided."
          rows={historyRows} />
      )}
      <Card>
        {error ? <div style={{ color: 'var(--crm-red)', fontSize: '12px' }}>{error}</div>
        : !k ? <div style={{ color: 'var(--crm-dim)', fontSize: '12px' }}>Loading…</div>
        : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text)' }}>{k.contractor?.name ?? 'No contractor assigned'}</span>
              {est && (
                <span style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>
                  Estimate {String(est.status).toUpperCase()} ·{' '}
                  {est.basis === 'final' ? `priced at the invoice's ${fmtBasis(est)}` : 'priced at the live lead weight / stored hours (not invoiced yet)'}
                </span>
              )}
            </div>

            {!est ? (
              <div style={{ fontSize: '12px', color: 'var(--crm-dim)' }}>No contractor estimate. Create one on Dispatch.</div>
            ) : (
              <>
                {approved && (
                  <div style={{ fontSize: '11px', color: 'var(--crm-amber)', marginBottom: '8px' }}>
                    Approved — a change here changes what the contractor is owed. Each change is kept in History (above) and the accounting log. Regenerate the work order on Dispatch afterwards.
                  </div>
                )}
                <ContractorLinesTable
                  calc={calc}
                  rowOf={(id) => lineState.get(id)}
                  editable
                  busy={!!busy}
                  lead={lead ?? {}}
                  final={final}
                  total={calc.total}
                  saving={sync.busy}
                  failed={sync.failed}
                  onCommit={(id, patch) => sync.edit(keyOfLine(id), patch)}
                  onRemove={(id) => sync.remove(keyOfLine(id))}
                  onRetry={(key) => sync.retry(key)}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value=""
                    onChange={(e) => { const v = e.target.value; e.target.value = ''; if (v) addFee(v); }}
                    disabled={!!busy}
                    style={{ padding: '7px 10px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', color: 'var(--crm-text)', borderRadius: '6px', fontSize: '12px' }}
                  >
                    <option value="">+ Add fee…</option>
                    {(fees ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  <button onClick={addCustom} disabled={!!busy} style={secondaryBtn}>+ Add custom line</button>
                  {est.canApplyLocalDefaults && (confirmDefaults ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--crm-muted)', flexWrap: 'wrap' }}>
                      Replace labour and fee lines with the local defaults? Custom lines stay.
                      <button onClick={applyDefaults} disabled={!!busy} style={primaryBtn}>{busy === 'defaults' ? 'Applying…' : 'Apply'}</button>
                      <button onClick={() => setConfirmDefaults(false)} disabled={!!busy} style={secondaryBtn}>Cancel</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirmDefaults(true)} disabled={!!busy} style={secondaryBtn} title="Labour at the tariff's contractor %, fuel and materials 100%, extra truck at its contractor amount, admin 0%">Apply local defaults</button>
                  ))}
                </div>
                <div style={{ fontFamily: mono, marginTop: '10px', fontSize: '12px' }}>
                  <Row label="Contractor estimate" value={formatMoney(calc.total)} />
                  <Row
                    label={tip.status === 'draft' ? 'Tip on the draft → crew 100% (owed once issued)' : 'Tip → crew 100% (untaxed, not on the estimate)'}
                    value={formatMoney(tip.amount)}
                  />
                  <Row label="Total to the contractor, tip included" value={formatMoney(totalWithTip)} bold />
                  <Row label="Paid to date on this job" value={formatMoney(k.paidToDate)} />
                  <Row label={approved ? 'Outstanding' : 'Outstanding (not approved yet — nothing owed)'} value={formatMoney(k.outstanding)} bold />
                </div>
              </>
            )}

            {k.preview?.local && (
              <div style={{ fontSize: '11px', color: 'var(--crm-muted)', marginTop: '10px' }}>
                {k.preview.approves ? 'Issuing the invoice approves this estimate' : 'On issue'}
                {k.preview.final_hours != null ? `, labour at the billed ${fmtBasis(k.preview)}` : ''}: about {formatMoney(k.preview.newTotal)} owed.
              </div>
            )}
            {k.preview && !k.preview.local && !k.pending && (
              <div style={{ fontSize: '11px', color: 'var(--crm-muted)', marginTop: '10px' }}>
                On issue, at the draft's {fmtBasis(k.preview)}: {formatMoney(k.preview.oldTotal)} → {formatMoney(k.preview.newTotal)}
                {k.preview.newTotal === k.preview.oldTotal ? ' (no change).'
                  : k.preview.waits ? ` — will wait for your acceptance (${k.preview.reason === 'approved' ? 'the estimate is approved' : 'it changes an hourly line'}).`
                  : ' — applied automatically (weight-based, not approved).'}
              </div>
            )}

            {k.pending && (
              <div style={{ background: 'var(--crm-amber-tint)', border: '1px solid var(--crm-amber-soft-line)', borderRadius: '8px', padding: '10px', marginTop: '12px' }}>
                <div style={{ fontSize: '12px', color: 'var(--crm-amber)', fontWeight: 600, marginBottom: '4px' }}>Contractor pay at the invoice's {fmtBasis(k.pending)}</div>
                <div style={{ fontSize: '13px', color: 'var(--crm-text)', fontFamily: mono, marginBottom: '8px' }}>
                  {formatMoney(k.pending.old_total)} → {formatMoney(k.pending.new_total)} ({num(k.pending.new_total) - num(k.pending.old_total) >= 0 ? '+' : '−'}{formatMoney(Math.abs(num(k.pending.new_total) - num(k.pending.old_total)))})
                </div>
                <div style={{ fontSize: '11px', color: 'var(--crm-muted)', marginBottom: '8px' }}>
                  Accepting changes what the contractor is owed and the job's recognized cost; the work order on file still shows the old total — regenerate it on Dispatch.
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button onClick={() => decide('accept', k.pending.id)} disabled={!!busy} style={primaryBtn}>{busy === 'accept' ? 'Accepting…' : `Accept ${formatMoney(k.pending.new_total)}`}</button>
                  <button onClick={() => decide('keep', k.pending.id)} disabled={!!busy} style={secondaryBtn}>
                    {busy === 'keep' ? 'Keeping…' : est?.status === 'approved' ? 'Keep approved total' : 'Keep current total'}
                  </button>
                </div>
              </div>
            )}
            {msg && <div style={{ marginTop: '8px' }}><Notice tone="red">{msg}</Notice></div>}

          </>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// Admin: Commission (phase 2, 049) — preview before issue, the record after
// ============================================================================

function CommissionSection({ leadId, cm, error, refresh }: { leadId: string; cm: any; error: string | null; refresh: () => Promise<void> }) {
  const api = useApiClient();
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<'pct' | 'flat'>('pct');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const startEdit = () => {
    setMode(cm?.override?.mode ?? 'pct');
    setValue(cm?.override ? String(cm.override.mode === 'flat' ? cm.override.amount : cm.override.pct) : '');
    setNote(cm?.override?.note ?? '');
    setMsg(null);
    setEditing(true);
  };
  const n = Number(value);
  const valid = value.trim() !== '' && Number.isFinite(n) && n >= 0 && (mode === 'flat' || n <= 100);
  const overrideGuard = useAction();
  const save = () => overrideGuard.run(() => saveNow());
  const clear = () => overrideGuard.run(() => clearNow());
  const saveNow = async () => {
    if (!valid) return;
    setBusy(true); setMsg(null);
    try {
      await api.setCommissionOverride(leadId, mode === 'pct' ? { mode, pct: n, note } : { mode, amount: n, note });
      setEditing(false);
      await refresh();
    } catch (e: any) { setMsg(e?.message || 'Could not save the override.'); }
    finally { setBusy(false); }
  };
  const clearNow = async () => {
    setBusy(true); setMsg(null);
    try { await api.clearCommissionOverride(leadId); setEditing(false); await refresh(); }
    catch (e: any) { setMsg(e?.message || 'Could not clear the override.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginBottom: '22px' }}>
      <h3 style={sectionHead}>Commission <span style={{ color: 'var(--crm-dim)', fontWeight: 400, textTransform: 'none' }}>· admin only</span></h3>
      <Card>
        {error ? <div style={{ color: 'var(--crm-red)', fontSize: '12px' }}>{error}</div>
        : !cm ? <div style={{ color: 'var(--crm-dim)', fontSize: '12px' }}>Loading…</div>
        : !cm.rep ? (
          <div style={{ fontSize: '12px', color: 'var(--crm-muted)' }}>No sales rep assigned — no commission on this job.</div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text)' }}>{cm.rep.name}</span>
              <span style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>
                Standard rate {cm.standardPct != null ? `${num(cm.standardPct)}%` : '0% (not set — Settings → Pricing & billing)'}
              </span>
            </div>

            <div style={{ fontSize: '12px', fontFamily: mono }}>
              {cm.base ? (
                <>
                  <Row label="Subtotal after adjustments (before tax, tip, CC fee)" value={formatMoney(cm.base.gross)} />
                  {num(cm.base.excludedDvp) > 0 && <Row label="Less the DVP premium" value={`−${formatMoney(cm.base.excludedDvp)}`} />}
                  <Row label="Commission base" value={formatMoney(cm.base.base)} bold />
                  <Row
                    label={cm.figure.method === 'flat' ? 'Flat amount (override)' : `${num(cm.figure.pct)}% ${cm.figure.source === 'override' ? '(override)' : '(standard)'}`}
                    value={formatMoney(cm.figure.amount)}
                    bold
                  />
                </>
              ) : (
                <div style={{ color: 'var(--crm-dim)', fontFamily: 'inherit' }}>No draft or Finalize invoice yet — the figure appears once the job is finalized.</div>
              )}
            </div>
            {cm.owed ? (
              <div style={{ fontSize: '12px', color: 'var(--crm-green)', marginTop: '6px' }}>
                Owed to {cm.owed.repName}: {formatMoney(cm.owed.amount)} — recorded {formatDate(cm.owed.recognitionDate)} (the invoice date)
                {cm.owed.paidAgainst > 0 ? ` · ${formatMoney(cm.owed.paidAgainst)} paid against it` : ''}.
                {Math.abs(num(cm.owed.amount) - num(cm.figure?.amount)) >= 0.005 && cm.figure && (
                  <span style={{ color: 'var(--crm-amber)' }}> The figure above uses today's rates; the recorded amount stands until the override changes.</span>
                )}
              </div>
            ) : cm.baseSource && (
              <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginTop: '4px' }}>
                From {cm.baseSource.label}. {cm.baseSource.kind === 'draft'
                  ? 'Preview — recorded as owed to the rep when the invoice is issued.'
                  : num(cm.figure?.amount) > 0 ? 'Not recorded for this invoice (issued before commissions were recorded, or with a 0 % rate then).' : 'Nothing is owed at this rate.'}
              </div>
            )}
            {(cm.records ?? []).some((r: any) => r.status === 'reversed') && (
              <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginTop: '4px' }}>A commission on this job was reversed — see History on the Contractor section.</div>
            )}

            {/* Override */}
            <div style={{ marginTop: '12px' }}>
              {!editing ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', color: cm.override ? 'var(--crm-amber)' : 'var(--crm-muted)' }}>
                    {cm.override
                      ? `Override: ${cm.override.mode === 'flat' ? `${formatMoney(cm.override.amount)} flat` : `${num(cm.override.pct)}%`}${cm.override.note ? ` — ${cm.override.note}` : ''}`
                      : 'No override — the standard rate applies.'}
                  </span>
                  <button onClick={startEdit} style={ghostBtn}>{cm.override ? 'Change' : 'Override for this job'}</button>
                  {cm.override && <button onClick={clear} disabled={busy} style={{ ...ghostBtn, color: 'var(--crm-red)', borderColor: 'var(--crm-red-soft-line)' }}>Clear</button>}
                </div>
              ) : (
                <div style={{ background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '8px', padding: '10px' }}>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                    <Pill active={mode === 'pct'} onClick={() => setMode('pct')}>Percentage</Pill>
                    <Pill active={mode === 'flat'} onClick={() => setMode('flat')}>Flat $</Pill>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px' }}>
                    <input {...numberInput(mode === 'pct' ? 0.01 : 1)} min={0} max={mode === 'pct' ? 100 : undefined} value={value} autoFocus
                      placeholder={mode === 'pct' ? '%' : '$'} onChange={(e) => setValue(e.target.value)} style={input} />
                    <input value={note} placeholder="Note (why)" onChange={(e) => setNote(e.target.value)} style={input} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                    <button onClick={() => setEditing(false)} disabled={busy} style={secondaryBtn}>Cancel</button>
                    <button onClick={save} disabled={!valid || busy} style={{ ...primaryBtn, opacity: valid ? 1 : 0.5 }}>{busy ? 'Saving…' : 'Save override'}</button>
                  </div>
                </div>
              )}
              {msg && <div style={{ marginTop: '8px' }}><Notice tone="red">{msg}</Notice></div>}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// Admin: Margin — computed server-side (backend lib/margin.ts), admin route only
// ============================================================================

function MarginSection({ m, error }: { m: any; error: string | null }) {
  const neg = (n: number) => `−${formatMoney(Math.abs(n))}`;
  const signed = (n: number) => (n < 0 ? neg(n) : `+${formatMoney(n)}`);
  return (
    <div style={{ marginBottom: '22px' }}>
      <h3 style={sectionHead}>Margin <span style={{ color: 'var(--crm-dim)', fontWeight: 400, textTransform: 'none' }}>· admin only</span></h3>
      <Card>
        {error ? <div style={{ color: 'var(--crm-red)', fontSize: '12px' }}>{error}</div>
        : !m ? <div style={{ color: 'var(--crm-dim)', fontSize: '12px' }}>Loading…</div>
        : (
          <>
            {m.source.preview && (
              <div style={{ fontSize: '11px', color: 'var(--crm-amber)', marginBottom: '8px' }}>
                Preview — from {m.source.label}. Final once the invoice is issued.
              </div>
            )}
            <div style={{ fontSize: '12px', fontFamily: mono }}>
              <Row label="Subtotal (after invoice adjustments; before tax, tip, CC fee)" value={formatMoney(m.subtotal)} />
              <Row label={m.hasContractorEstimate ? 'Contractor (current estimate; tips pass through)' : 'Contractor (no estimate yet)'} value={neg(m.contractor)} />
              <Row label={`Sales commission${m.commissionState === 'preview' ? ' (preview)' : m.commissionState === 'none' ? ' (none)' : ''}`} value={neg(m.commission)} />
              <Row
                label={`Card processing (net) — processor ${formatMoney(m.card.processorCost)} less CC fees ${formatMoney(m.card.ccFees)}`}
                value={m.card.net > 0 ? neg(m.card.net) : `+${formatMoney(Math.abs(m.card.net))}`}
              />
              {(m.adjustments ?? []).length === 0 && <Row label="Job adjustments (none on this job)" value={formatMoney(0)} />}
              {(m.adjustments ?? []).map((a: any) => (
                <Row key={a.id} label={`Job adjustment · ${formatDate(a.date)} · ${a.description}`} value={signed(a.effect)} />
              ))}
              <div style={{ borderTop: '1px solid var(--crm-line)', marginTop: '6px', paddingTop: '6px' }}>
                <Row label={`Margin before taxes${m.marginPct != null ? ` (${m.marginPct}% of subtotal)` : ''}`} value={formatMoney(m.marginBeforeTax)} bold big />
              </div>
              <Row label="Taxes charged" value={`+${formatMoney(m.tax)}`} />
              <Row label="Margin incl. tax collected" value={formatMoney(m.marginInclTax)} bold />
            </div>
            <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginTop: '8px' }}>
              Job adjustments are Accounting → Adjustments recorded against this job; each counts by its effect on the partners.
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// Small pieces
// ============================================================================

/**
 * A number field that keeps what is being typed ("-", "5.") on screen and only
 * commits a parseable number (or null when cleared), so a half-typed value is
 * never sent as a bad PATCH.
 */
// ---- Local hourly: final crew and time (055) ----------------------------------

/** The Finalize time of a local job, in the units it is entered in. */
interface FinalTimeValue {
  travelMin: number | null;
  onsiteMin: number | null;
  minimumHours: number | null;
  /** The rep's billable hours; null = calculated. */
  billableOverride: number | null;
  crew: number | null;
  trucks: number | null;
}

const toMin = (h: number | null | undefined) => (h == null ? null : Math.round(num(h) * 60));

/** Pre-filled from the estimate: its travel, on-site, minimum, crew and trucks. */
const openingTime = (o: HourlyOpening): FinalTimeValue => ({
  travelMin: toMin(o.travelHours),
  onsiteMin: toMin(o.onsiteHours),
  minimumHours: o.minimumHours,
  billableOverride: null,
  crew: o.crewSize != null ? num(o.crewSize) : null,
  trucks: o.trucks,
});

/** The draft's figures; crew, trucks and minimum not changed on it show the estimate's. */
const draftTime = (r: DraftRow, e: HourlyOpening): FinalTimeValue => ({
  travelMin: toMin(r.final_travel_hours),
  onsiteMin: toMin(r.final_onsite_hours),
  minimumHours: r.final_minimum_hours ?? e.minimumHours,
  billableOverride: r.final_billable_hours,
  crew: r.final_crew_size ?? (e.crewSize != null ? num(e.crewSize) : null),
  trucks: r.final_trucks ?? e.trucks,
});

const draftTimePatch = (p: Partial<FinalTimeValue>): Partial<DraftRow> => {
  const out: Partial<DraftRow> = {};
  if ('travelMin' in p) out.final_travel_hours = p.travelMin == null ? null : p.travelMin / 60;
  if ('onsiteMin' in p) out.final_onsite_hours = p.onsiteMin == null ? null : p.onsiteMin / 60;
  if ('minimumHours' in p) out.final_minimum_hours = p.minimumHours ?? null;
  if ('billableOverride' in p) out.final_billable_hours = p.billableOverride ?? null;
  if ('crew' in p) out.final_crew_size = p.crew ?? null;
  if ('trucks' in p) out.final_trucks = p.trucks ?? null;
  return out;
};

/** The engine's rule, so this card shows exactly what calcFinal bills. */
const billedOf = (v: FinalTimeValue) => finalBilledHours({
  finalTravelHours: v.travelMin == null ? null : v.travelMin / 60,
  finalOnsiteHours: v.onsiteMin == null ? null : v.onsiteMin / 60,
  finalMinimumHours: v.minimumHours,
  finalBillableHours: v.billableOverride,
});

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

const FINAL_TIME_CSS = `
.fz-time { display:grid; grid-template-columns:minmax(0, 1fr); gap:10px; }
@media (min-width:560px) { .fz-time { grid-template-columns:minmax(0, 1fr) minmax(0, 1.4fr); } }
`;

/**
 * CREW and TIME for a local job's Finalize — the estimate's "Time" card
 * (ebc4054), on the final figures: travel and on-site in hours + minutes, the
 * minimum, and the billable time, calculated as the estimate calculates it
 * (rounded UP to 15 minutes, never below the minimum) unless the rep overrides
 * it. An override is marked, and ↺ goes back to the calculated figure.
 */
function FinalTimeCards({ value: v, estimate: e, onChange }: {
  value: FinalTimeValue;
  estimate: HourlyOpening;
  onChange: (patch: Partial<FinalTimeValue>) => void;
}) {
  const t = billedOf(v);
  const worked = t.workedHours;
  const minimumApplies = !t.overridden && worked != null && t.minimumHours != null && t.minimumHours > roundUpToQuarterHour(worked);
  const estTrucks = e.trucks ?? 1;
  const trucks = v.trucks ?? estTrucks;
  const intOrNull = (raw: string) => (raw.trim() === '' ? null : Math.max(1, Math.round(Number(raw) || 0)));
  const commitBillable = (raw: string) => {
    if (raw.trim() === '') { onChange({ billableOverride: null }); return; }
    const h = Math.round(Math.max(0, Number(raw) || 0) * 60) / 60;
    if (!(h > 0)) return;
    // Typing the calculated figure back is not an override.
    onChange({ billableOverride: !t.overridden && h === t.calculatedHours ? null : h });
  };
  const row = (label: string, input: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ ...fieldLabel, width: '58px', flexShrink: 0 }}>{label}</span>
      {input}
    </div>
  );
  const estHint = e.hours != null
    ? `Estimate: ${hmShort(toMin(e.travelHours) ?? 0)} travel + ${hmShort(toMin(e.onsiteHours) ?? 0)} on site${e.minimumHours ? `, ${e.minimumHours} h minimum` : ''}`
    : 'The estimate had no hours.';
  return (
    <div className="fz-time">
      <style>{FINAL_TIME_CSS}</style>

      <div style={cardStyle}>
        <div style={cardTitle}>Crew</div>
        {row('Movers', <InlineInput value={v.crew ?? ''} type="number" step={1} width="52px" onCommit={(raw) => onChange({ crew: intOrNull(raw) })} />)}
        {row('Trucks', <InlineInput value={v.trucks ?? ''} type="number" step={1} width="52px" onCommit={(raw) => onChange({ trucks: intOrNull(raw) })} />)}
        <div style={{ fontSize: '11px', color: 'var(--crm-dim)' }}>
          Estimate: {[e.crewSize ? plural(num(e.crewSize), 'mover') : null, e.trucks ? plural(e.trucks, 'truck') : null].filter(Boolean).join(', ') || 'crew not recorded'}
        </div>
        {trucks !== estTrucks && (e.hasAdditionalTruckFee ? (
          <div style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>
            {trucks >= 2 ? `Additional Truck × ${trucks - 1} on the invoice.` : 'No Additional Truck on the invoice.'}
          </div>
        ) : trucks >= 2 && (
          <div style={{ fontSize: '11px', color: 'var(--crm-amber)' }}>
            No "Additional Truck" fee in Settings → Fees, so the extra truck is not charged. Add an adjustment instead.
          </div>
        ))}
      </div>

      <div style={cardStyle}>
        <div style={cardTitle}>Time</div>
        {row('Travel', <HoursMinutesInput minutes={v.travelMin} onCommit={(m) => onChange({ travelMin: m })} />)}
        {row('On-site', <HoursMinutesInput minutes={v.onsiteMin} onCommit={(m) => onChange({ onsiteMin: m })} />)}
        {row('Minimum', (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <InlineInput value={v.minimumHours ?? ''} type="number" step={0.25} width="56px"
              onCommit={(raw) => onChange({ minimumHours: raw.trim() === '' ? null : Math.max(0, Math.round((Number(raw) || 0) * 4) / 4) })} />
            <span style={unitLabel}>h</span>
          </span>
        ))}
        <div style={{ fontSize: '11px', color: 'var(--crm-dim)' }}>{estHint}</div>
        <div style={{ borderTop: '1px solid var(--crm-line)', paddingTop: '9px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>
            Total {worked != null ? hmShort(Math.round(worked * 60)) : '—'}
            {minimumApplies && ' · minimum applies'}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={fieldLabel}>Billable</span>
            <InlineInput value={t.billedHours != null ? t.billedHours.toFixed(2) : ''} type="number" step={0.25} width="72px" highlight={t.overridden} onCommit={commitBillable} />
            <span style={{ fontSize: '12px', color: 'var(--crm-muted)' }}>hrs</span>
          </span>
        </div>
        {t.overridden && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', fontSize: '11px' }}>
            <span style={{ color: 'var(--crm-amber)' }}>
              Overridden{t.calculatedHours != null ? ` — calculated ${t.calculatedHours.toFixed(2)} hrs` : ''}
            </span>
            <button onClick={() => onChange({ billableOverride: null })} title="Back to the calculated billable time"
              style={{ ...ghostBtn, color: 'var(--crm-amber)', borderColor: 'var(--crm-amber-soft-line)' }}>
              ↺ Use calculated
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function NumInput({ value, onCommit, step, allowNegative = false }: {
  value: number | string | null | undefined; onCommit: (v: number | null) => void; step?: number; allowNegative?: boolean;
}) {
  const [text, setText] = useState(value == null ? '' : String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(value == null ? '' : String(value)); }, [value, focused]);
  return (
    <input
      {...numberInput(step ?? 1, { negative: allowNegative })}
      min={allowNegative ? undefined : 0}
      value={text}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        if (t.trim() === '') { onCommit(null); return; }
        const n = Number(t);
        if (Number.isFinite(n) && (allowNegative || n >= 0)) onCommit(n);
      }}
      style={input}
    />
  );
}

function SaveState({ status, error, busy, onRetry }: { status: string; error: string | null; busy: boolean; onRetry: () => void }) {
  if (status === 'error') {
    return (
      <span style={{ fontSize: '11px', color: 'var(--crm-red)' }}>
        Not saved{error ? `: ${error}` : ''}{' '}
        <button onClick={onRetry} style={{ ...ghostBtn, fontSize: '10px', padding: '2px 7px' }}>Retry</button>
      </span>
    );
  }
  return <span style={{ fontSize: '11px', color: busy ? 'var(--crm-amber)' : 'var(--crm-dim)' }}>{busy ? 'Saving…' : 'Saved'}</span>;
}

function Card({ children }: { children: React.ReactNode }) {
  return <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', padding: '14px' }}>{children}</div>;
}

function Field({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ marginBottom: '10px', ...style }}>
      <label style={{ ...labelStyle, display: 'block', marginBottom: '5px' }}>{label}</label>
      {children}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginTop: '4px' }}>{children}</div>;
}

function Notice({ tone, children }: { tone: 'red' | 'amber'; children: React.ReactNode }) {
  const c = tone === 'red' ? 'var(--crm-red)' : 'var(--crm-amber)';
  return <div style={{ fontSize: '12px', color: c, background: `${tintOf(c, 8)}`, border: `1px solid ${tintOf(c, 33)}`, borderRadius: '8px', padding: '8px 10px', marginBottom: '8px' }}>{children}</div>;
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{ padding: '5px 11px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', border: `1px solid ${active ? 'var(--crm-accent-line)' : 'var(--crm-line)'}`, background: active ? 'var(--crm-accent-tint)' : 'var(--crm-panel-2)', color: active ? 'var(--crm-text)' : 'var(--crm-muted)' }}
    >
      {children}
    </button>
  );
}

function Row({ label, value, bold = false, big = false }: { label: string; value: string; bold?: boolean; big?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '2px 0' }}>
      <span style={{ color: 'var(--crm-muted)' }}>{label}</span>
      <span style={{ color: 'var(--crm-text)', fontWeight: bold ? 700 : 400, fontSize: big ? '15px' : undefined }}>{value}</span>
    </div>
  );
}

/**
 * Right after issue: "Send it to the client now?". The send is the invoice
 * list's own Send to client — the batch endpoint with this one id — so the
 * email, the audit and the 'sent' status are the same, and a suppressed
 * address offers "Send anyway" the same way. Reps and admins alike.
 */
function SendIssuedModal({ leadId, docId, email, onSent, onClose }: {
  leadId: string; docId: string; email: string; onSent: () => void; onClose: () => void;
}) {
  const api = useApiClient();
  const sendGuard = useAction();
  const [msg, setMsg] = useState<{ ok: boolean; text: string; onOverride?: () => void } | null>(null);
  const [sent, setSent] = useState(false);
  const send = (override = false) => sendGuard.run((key) => sendNow(override, key));
  const sendNow = async (override: boolean, key: string) => {
    setMsg(null);
    try {
      const res = await api.withIdempotencyKey(key).sendDocumentBatch(leadId, [docId], override);
      const d = res.data!;
      onSent();
      if (d.emailed) { setSent(true); setMsg({ ok: true, text: `Invoice emailed to ${email}.` }); }
      else setMsg({ ok: false, text: d.failed?.[0]?.reason ?? d.reason ?? 'Not sent.' });
    } catch (e: any) {
      setMsg({
        ok: false,
        text: e?.message || 'Send failed — please try again.',
        onOverride: e?.code === 'SUPPRESSED' ? () => send(true) : undefined,
      });
    }
  };
  const busy = sendGuard.pending;

  return (
    <Modal title="Invoice issued" onClose={busy ? () => {} : onClose}>
      <div style={{ fontSize: '13px', color: 'var(--crm-text)', marginBottom: '14px' }}>
        {email ? <>Send it to <span style={{ color: 'var(--crm-text)', fontWeight: 600 }}>{email}</span> now?</> : 'No email on file for this client.'}
      </div>
      {msg && (
        <div style={{ fontSize: '12px', color: msg.ok ? 'var(--crm-green)' : 'var(--crm-red)', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '14px' }}>
          <span>{msg.text}</span>
          {msg.onOverride && (
            <button onClick={msg.onOverride} disabled={busy} style={{ padding: '3px 10px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-amber-line)', color: 'var(--crm-amber)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>Send anyway</button>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        {email && !sent ? (
          <>
            <button onClick={onClose} disabled={busy} style={secondaryBtn}>Not now</button>
            <button onClick={() => send()} disabled={busy} style={primaryBtn}>{busy ? 'Sending…' : 'Send'}</button>
          </>
        ) : (
          <button onClick={onClose} style={secondaryBtn}>Close</button>
        )}
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children, width = '400px' }: { title: string; onClose: () => void; children: React.ReactNode; width?: string }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '12px', padding: '20px', width, maxWidth: '100%', maxHeight: '100%', overflowY: 'auto', boxSizing: 'border-box', boxShadow: '0 20px 60px var(--crm-shadow-color)' }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--crm-text)', marginBottom: '16px' }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

const mono = "'DM Mono', monospace";
const sectionHead: React.CSSProperties = { fontSize: '11px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase', margin: '0 0 12px' };
const labelStyle: React.CSSProperties = { fontSize: '10px', fontWeight: 600, color: 'var(--crm-muted)', textTransform: 'uppercase' };
const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px', color: 'var(--crm-text)', fontSize: '13px', padding: '7px 9px' };
const primaryBtn: React.CSSProperties = { padding: '7px 14px', background: 'var(--crm-accent-fill)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' };
const secondaryBtn: React.CSSProperties = { padding: '7px 14px', background: 'var(--crm-panel-2)', color: 'var(--crm-muted)', border: '1px solid var(--crm-line)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' };
const ghostBtn: React.CSSProperties = { padding: '4px 9px', background: 'transparent', color: 'var(--crm-muted)', border: '1px solid var(--crm-line)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', whiteSpace: 'nowrap' };
